import type {CampoDore, DatosDore, ExportacionDore} from '@cnc/contracts';
import {tr, trx, txt, type Texto} from '../i18n/idioma';

// Campos de una exportacion de dore que se editan en la app (decision 0094):
// los usan la edicion de la ficha y la exportacion nueva.
type TipoDeCampo = 'texto' | 'numero' | 'entero' | 'fecha';
type ListaDore = 'estados' | 'proveedores' | 'intermediarias' | 'compradores';
type CampoDeDore = {campo: Exclude<CampoDore, 'comentarios'>; etiqueta: Texto; tipo: TipoDeCampo; lista?: ListaDore};

export const GRUPOS_DORE: Array<{titulo: Texto; campos: CampoDeDore[]}> = [
  {
    titulo: txt('Venta', 'Sale'),
    campos: [
      {campo: 'proveedor', etiqueta: txt('Proveedor', 'Supplier'), tipo: 'texto', lista: 'proveedores'},
      {campo: 'intermediaria', etiqueta: txt('Intermediaria', 'Intermediary'), tipo: 'texto', lista: 'intermediarias'},
      {campo: 'compradorFinal', etiqueta: txt('Comprador final', 'Final buyer'), tipo: 'texto', lista: 'compradores'},
      {campo: 'referencia', etiqueta: txt('Referencia', 'Reference'), tipo: 'texto'},
      {campo: 'pais', etiqueta: txt('País de destino', 'Destination country'), tipo: 'texto'},
      {campo: 'compra', etiqueta: txt('Compra (NEO)', 'Purchase (NEO)'), tipo: 'texto'},
      {campo: 'ventaDelProveedor', etiqueta: txt('Venta del proveedor', 'Supplier sale'), tipo: 'texto'},
      {campo: 'facturaProveedor', etiqueta: txt('Factura del proveedor', 'Supplier invoice'), tipo: 'texto'},
      {campo: 'exportacionProveedor', etiqueta: txt('Exportación del proveedor', 'Supplier export'), tipo: 'texto'},
      {campo: 'valorProveedorUsd', etiqueta: txt('Valor al proveedor (USD)', 'Supplier value (USD)'), tipo: 'numero'},
      {campo: 'valorVentaUsd', etiqueta: txt('Valor de la venta (USD)', 'Sale value (USD)'), tipo: 'numero'},
      {campo: 'estadoArchivo', etiqueta: txt('Estado', 'Status'), tipo: 'texto', lista: 'estados'},
    ],
  },
  {
    titulo: txt('Material', 'Material'),
    campos: [
      {campo: 'barras', etiqueta: txt('Barras', 'Bars'), tipo: 'entero'},
      {campo: 'barrasDePlata', etiqueta: txt('Barras de plata', 'Silver bars'), tipo: 'entero'},
      {campo: 'brutoG', etiqueta: txt('Peso bruto (g)', 'Gross weight (g)'), tipo: 'numero'},
      {campo: 'oroG', etiqueta: txt('Oro (g)', 'Gold (g)'), tipo: 'numero'},
      {campo: 'plataG', etiqueta: txt('Plata (g)', 'Silver (g)'), tipo: 'numero'},
      {campo: 'tipoDeMaterial', etiqueta: txt('Tipo', 'Type'), tipo: 'texto'},
    ],
  },
  {
    titulo: txt('Fechas, transporte y aduana', 'Dates, transport and customs'),
    campos: [
      {campo: 'fechaDocumentos', etiqueta: txt('Documentos', 'Documents'), tipo: 'fecha'},
      {campo: 'fechaExportacion', etiqueta: txt('Exportación', 'Export date'), tipo: 'fecha'},
      {campo: 'llegada', etiqueta: txt('Llegada', 'Arrival'), tipo: 'fecha'},
      {campo: 'llegadaRefineria', etiqueta: txt('Llegada a la refinería', 'Arrival at refinery'), tipo: 'fecha'},
      {campo: 'servicio', etiqueta: txt('Servicio', 'Service'), tipo: 'texto'},
      {campo: 'transportadora', etiqueta: txt('Transportadora', 'Carrier'), tipo: 'texto'},
      {campo: 'mawb', etiqueta: txt('MAWB', 'MAWB'), tipo: 'texto'},
      {campo: 'hawb', etiqueta: txt('HAWB', 'HAWB'), tipo: 'texto'},
      {campo: 'mawbConexion', etiqueta: txt('MAWB de conexión', 'Connecting MAWB'), tipo: 'texto'},
      {campo: 'radicadoVuce', etiqueta: txt('Radicado VUCE', 'VUCE filing'), tipo: 'texto'},
      {campo: 'aprobacionVuce', etiqueta: txt('Aprobación VUCE', 'VUCE approval'), tipo: 'fecha'},
    ],
  },
];

const CAMPOS = GRUPOS_DORE.flatMap(g => g.campos);
export type ValoresDore = Record<CampoDore, string>;

// Valores de una exportacion como texto para el formulario (vacio = sin dato).
export function valoresDeDore(d: Partial<ExportacionDore> | null): ValoresDore {
  const valores = {} as ValoresDore;
  for (const c of [...CAMPOS.map(x => x.campo), 'comentarios'] as CampoDore[]) {
    const v = d?.[c];
    valores[c] = v === null || v === undefined ? '' : String(v);
  }
  return valores;
}

// Lo que cambio frente a los valores iniciales, convertido para la API
// (numeros con coma o punto; vacio borra el dato).
export function cambiosDeDore(
  valores: ValoresDore,
  iniciales: ValoresDore,
): {cambios: Partial<DatosDore>} | {error: string} {
  const cambios: Record<string, string | number | null> = {};
  for (const c of [
    ...CAMPOS,
    {campo: 'comentarios' as const, etiqueta: txt('Comentarios', 'Comments'), tipo: 'texto'},
  ]) {
    const nuevo = (valores[c.campo] ?? '').trim();
    if (nuevo === (iniciales[c.campo] ?? '').trim()) continue;
    if (nuevo === '' || c.tipo === 'texto' || c.tipo === 'fecha') {
      cambios[c.campo] = nuevo || null;
      continue;
    }
    const numero = Number(nuevo.replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(numero) || numero < 0 || (c.tipo === 'entero' && !Number.isInteger(numero)))
      return {
        error:
          c.tipo === 'entero'
            ? tr(`${trx(c.etiqueta)}: escribe un número entero.`, `${trx(c.etiqueta)}: enter a whole number.`)
            : tr(`${trx(c.etiqueta)}: escribe un número.`, `${trx(c.etiqueta)}: enter a number.`),
      };
    if (numero === Number(iniciales[c.campo])) continue;
    cambios[c.campo] = numero;
  }
  return {cambios: cambios as Partial<DatosDore>};
}
